import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import { errorAlert } from "../../Components/Alerts";
import CompanyHeader from "../FinancialStatementCRUD/FinancialS_Components/CompanyHeader";
import "./VATMonthlySyntheticPage.scss";

const MONTH_LABELS = {
  1: "Janar",
  2: "Shkurt",
  3: "Mars",
  4: "Prill",
  5: "Maj",
  6: "Qershor",
  7: "Korrik",
  8: "Gusht",
  9: "Shtator",
  10: "Tetor",
  11: "Nentor",
  12: "Dhjetor",
};

const formatAmount = (value) => {
  const amount = Number(value) || 0;
  return amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

export default function VATMonthlySyntheticPage() {
  const { fiscalYear } = useContext(FiscalYearContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isGeneratingPrint, setIsGeneratingPrint] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const headers = authHeader();
        const response = await axios.get(ApiLink.API_URL + "TaxReport/VATMonthlySynthetic", {
          headers,
          params: { fiscalYear },
        });

        if (Array.isArray(response.data)) {
          setRows(
            response.data.map((item) => ({
              monthNo: Number(item.MonthNo),
              monthName: MONTH_LABELS[Number(item.MonthNo)] || "-",
              openingBalance: Number(item.OpeningBalance) || 0,
              debit108: Number(item.Debit108) || 0,
              credit303: Number(item.Credit303) || 0,
              paymentAmount: Number(item.PaymentAmount) || 0,
              baseBalance: (Number(item.Debit108) || 0) - (Number(item.Credit303) || 0),
            }))
          );
        } else {
          setRows([]);
        }
      } catch (error) {
        errorAlert("Gabim në kërkesë:", error);
        if (error.response?.data?.errorMessage) {
          errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
        } else if (error.message) {
          errorAlert(`Gabim: ${error.message}`);
        } else {
          errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fiscalYear]);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const headers = authHeader();
        const response = await axios.get(ApiLink.API_URL + "Companies/getCompanyDetails", { headers });
        if (response.data && response.data.length > 0) {
          setCompanyInfo(response.data[0]);
        }
      } catch (error) {
        // Keep PDF generation resilient even if company info endpoint fails.
      }
    };

    fetchCompany();
  }, []);

  const totals = useMemo(() => {
    const rowsWithCarry = [];
    let carryBalance = 0;
    let openingBalanceJanar = 0;

    rows.forEach((row) => {
      const monthBaseWithOpening = row.baseBalance + (row.monthNo === 1 ? row.openingBalance : 0);

      if (row.monthNo === 1) {
        openingBalanceJanar = row.openingBalance;
      }

      // Bartet vetëm salldoja pozitive e muajit paraprak; salldoja negative nuk bartet.
      carryBalance = (carryBalance > 0 ? carryBalance : 0) + monthBaseWithOpening;

      rowsWithCarry.push({
        ...row,
        balance: carryBalance,
      });
    });

    return {
      rowsWithCarry,
      openingBalanceJanar,
      debit108: rowsWithCarry.reduce((sum, row) => sum + row.debit108, 0),
      credit303: rowsWithCarry.reduce((sum, row) => sum + row.credit303, 0),
      paymentTrs303: rowsWithCarry.reduce((sum, row) => sum + row.paymentAmount, 0),
    };
  }, [rows]);

  const summaryFormulaBalance =
    totals.openingBalanceJanar + totals.debit108 - totals.credit303 + totals.paymentTrs303;

  const buildMonthlySyntheticPdfDoc = () => {
    if (!totals.rowsWithCarry.length) return null;

    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const generatedAt = formatDate(new Date());

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(companyInfo?.CompanyName || "Kompania", 105, 12, { align: "center" });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Raporti Mujor Sintetik i TVSH-së", 105, 18, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.text("Monthly VAT Synthetic Report", 105, 22.5, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(`Numri fiskal: ${companyInfo?.IdentificationNumber || "-"}`, 10, 27.5);
    doc.text(`Adresa: ${companyInfo?.Address || "-"}`, 10, 31.5);
    doc.text(`Periudha: Viti ${fiscalYear}`, 200, 27.5, { align: "right" });
    doc.text(`Bilanci fillestar (108): ${formatAmount(totals.openingBalanceJanar)}`, 200, 31.5, { align: "right" });

    const bodyRows = totals.rowsWithCarry.map((row) => [
      row.monthName,
      formatAmount(row.debit108),
      formatAmount(row.credit303),
      formatAmount(row.balance),
    ]);

    autoTable(doc, {
      startY: 36,
      head: [["Muaji", "Debi (108 TVSH e arketueshme)", "303 TVSH e pagueshme", "Bilanci"]],
      body: bodyRows,
      tableWidth: 190,
      theme: "grid",
      styles: {
        fontSize: 7.8,
        cellPadding: 1.2,
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
        textColor: [33, 33, 33],
      },
      headStyles: {
        fillColor: [40, 73, 69],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
      },
      columnStyles: {
        0: { cellWidth: 58 },
        1: { cellWidth: 44, halign: "right" },
        2: { cellWidth: 44, halign: "right" },
        3: { cellWidth: 44, halign: "right" },
      },
      didParseCell: (hookData) => {
        if (hookData.section === "body" && hookData.row.index % 2 === 1) {
          hookData.cell.styles.fillColor = [252, 253, 252];
        }
      },
      margin: { top: 10, right: 10, bottom: 12, left: 10 },
      pageBreak: "auto",
    });

    const summaryRows = [
      ["1. Bilanci Fillestar", formatAmount(totals.openingBalanceJanar)],
      ["2. Total Debit (TVSH e Arketueshme)", formatAmount(totals.debit108)],
      ["3. Total Kredit (TVSH e pagueshme)", formatAmount(totals.credit303)],
      ["4. Pagesat", formatAmount(totals.paymentTrs303)],
      ["5. Bilanci = 1 + 2 - 3 + 4", formatAmount(summaryFormulaBalance)],
    ];

    autoTable(doc, {
      startY: (doc.lastAutoTable?.finalY || 36) + 4,
      body: summaryRows,
      tableWidth: 190,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 1.8,
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
        textColor: [33, 33, 33],
      },
      columnStyles: {
        0: { cellWidth: 142, fontStyle: "bold" },
        1: { cellWidth: 48, halign: "right", fontStyle: "bold" },
      },
      didParseCell: (hookData) => {
        if (hookData.section !== "body") return;
        if (hookData.row.index === 4) {
          hookData.cell.styles.fillColor = [233, 241, 237];
          hookData.cell.styles.textColor = [31, 58, 54];
        } else if (hookData.row.index % 2 === 1) {
          hookData.cell.styles.fillColor = [252, 253, 252];
        }
      },
      margin: { top: 10, right: 10, bottom: 12, left: 10 },
      pageBreak: "auto",
    });

    const totalPages = doc.internal.getNumberOfPages();
    for (let page = 1; page <= totalPages; page++) {
      doc.setPage(page);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Faqe ${page}/${totalPages}`, 8, 292);
      doc.text(`Gjeneruar më ${generatedAt} • Accountree`, 202, 292, { align: "right" });
    }

    return doc;
  };

  const handleDownloadPdf = () => {
    if (isGeneratingPdf || isGeneratingPrint) return;

    if (!totals.rowsWithCarry.length) {
      window.alert("Nuk ka të dhëna për PDF.");
      return;
    }

    setIsGeneratingPdf(true);
    try {
      const doc = buildMonthlySyntheticPdfDoc();
      if (!doc) {
        window.alert("Gabim gjatë ndërtimit të PDF.");
        return;
      }
      doc.save(`VAT_Monthly_Synthetic_${fiscalYear}.pdf`);
    } catch (error) {
      window.alert("Ndodhi gabim gjatë gjenerimit të PDF.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handlePrintPdf = () => {
    if (isGeneratingPdf || isGeneratingPrint) return;

    if (!totals.rowsWithCarry.length) {
      window.alert("Nuk ka të dhëna për printim.");
      return;
    }

    setIsGeneratingPrint(true);
    try {
      const doc = buildMonthlySyntheticPdfDoc();
      if (!doc) {
        window.alert("Gabim gjatë ndërtimit të dokumentit për printim.");
        return;
      }
      const blobUrl = doc.output("bloburl");

      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = blobUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(blobUrl);
        }, 1000);
      };
    } catch (error) {
      window.alert("Ndodhi gabim gjatë printimit.");
    } finally {
      setIsGeneratingPrint(false);
    }
  };

  return (
    <div className="VATMS__Page">
      <div className="VATMS__Header">
        <CompanyHeader
          reportTitle="Raporti Vjetor Sintetik i TVSH-së"
          reportSubtitle="Debi 108 TVSH e arketueshme, 303 TVSH e pagueshme dhe bilanci"
          fiscalYear={fiscalYear}
          onDownloadPdf={handleDownloadPdf}
          isGeneratingPdf={isGeneratingPdf}
          onPrint={handlePrintPdf}
          isGeneratingPrint={isGeneratingPrint}
        />
      </div>

      <section className="VATMS__Card">
        <div className="VATMS__CardHead">
          <h3>TVSH Vjetore</h3>
          <p>Pasqyrë sintetike sipas muajve për vitin fiskal {fiscalYear}</p>
        </div>

        {loading ? (
          <div className="VATMS__Loading">Duke ngarkuar të dhënat...</div>
        ) : (
          <div className="VATMS__TableWrap">
            <div className="VATMS__OpeningRow">
              <span className="VATMS__OpeningLabel">Bilanci fillestar nga ACS (TVSH e arketueshme)</span>
              <span className={`VATMS__OpeningValue ${totals.openingBalanceJanar < 0 ? "is-negative" : "is-positive"}`}>
                {formatAmount(totals.openingBalanceJanar)}
              </span>
            </div>

            <table className="VATMS__Table">
              <thead>
                <tr>
                  <th>Muaji</th>
                  <th>TVSH e arketueshme</th>
                  <th>TVSH e pagueshme</th>
                  <th>Bilanci</th>
                </tr>
              </thead>
              <tbody>
                {totals.rowsWithCarry.map((row) => {
                  const isNegative = row.balance < 0;
                  return (
                    <tr key={row.monthNo} className={isNegative ? "VATMS__Row VATMS__Row--negative" : "VATMS__Row VATMS__Row--positive"}>
                      <td>
                        <div className="VATMS__MonthCell">
                          <span className="VATMS__MonthNo">{String(row.monthNo).padStart(2, "0")}</span>
                          <span className="VATMS__MonthTag">{row.monthName}</span>
                        </div>
                      </td>
                      <td className="VATMS__Num">{formatAmount(row.debit108)}</td>
                      <td className="VATMS__Num">{formatAmount(row.credit303)}</td>
                      <td className="VATMS__Num">
                        <span className={`VATMS__BalanceBadge ${isNegative ? "is-negative" : "is-positive"}`}>
                          {formatAmount(row.balance)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="VATMS__SummaryPanel">
              <div className="VATMS__SummaryCards">
                <div className="VATMS__SummaryCard">
                  <span className="VATMS__SummaryCardLabel">1. Bilanci Fillestar</span>
                  <span className={`VATMS__SummaryCardValue ${totals.openingBalanceJanar < 0 ? "is-negative" : "is-positive"}`}>
                    {formatAmount(totals.openingBalanceJanar)}
                  </span>
                </div>

                <div className="VATMS__SummaryCard">
                  <span className="VATMS__SummaryCardLabel">2. Total Debit (TVSH e Arketueshme)</span>
                  <span className="VATMS__SummaryCardValue is-positive">{formatAmount(totals.debit108)}</span>
                </div>

                <div className="VATMS__SummaryCard">
                  <span className="VATMS__SummaryCardLabel">3. Total Kredit (TVSH e pagueshme)</span>
                  <span className="VATMS__SummaryCardValue is-negative">{formatAmount(totals.credit303)}</span>
                </div>

                <div className="VATMS__SummaryCard">
                  <span className="VATMS__SummaryCardLabel">4. Pagesat</span>
                  <span className="VATMS__SummaryCardValue is-positive">{formatAmount(totals.paymentTrs303)}</span>
                </div>
              </div>

              <div className="VATMS__SummaryFormula">
                <span className="VATMS__SummaryFormulaLabel">5. Bilanci = 1 + 2 - 3 + 4</span>
                <span className={`VATMS__SummaryFormulaValue ${summaryFormulaBalance < 0 ? "is-negative" : "is-positive"}`}>
                  {formatAmount(summaryFormulaBalance)}
                </span>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
